import React, { useEffect, useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { UploadSourceMobileCamera } from '@/types/platform';
import { PLATFORM_URL } from '@/config/api';
import {
  createMobileUploadSession,
  getMobileUploadStatus,
  downloadMobileUploadedFile,
  cancelMobileUploadSession,
  MobileUploadSession,
} from '@/services/mobileUploadService';
import './MobileUploadQrModal.css';

interface MobileUploadQrModalProps {
  config: UploadSourceMobileCamera;
  getTranslation: (key: string) => string;
  onFilesReceived: (files: File[]) => void;
  onClose: () => void;
}

type ModalState = 'loading' | 'waiting' | 'received' | 'error' | 'expired';

const MobileUploadQrModal: React.FC<MobileUploadQrModalProps> = ({
  config,
  getTranslation,
  onFilesReceived,
  onClose,
}) => {
  const [state, setState] = useState<ModalState>('loading');
  const [session, setSession] = useState<MobileUploadSession | null>(null);
  const [qrUrl, setQrUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<MobileUploadSession | null>(null);
  const unmountedRef = useRef(false);

  // Keep session ref up to date for cleanup
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Cleanup on unmount
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stopPolling();
      // Cancel session if still active
      if (sessionRef.current) {
        cancelMobileUploadSession(config.cancelEndpoint, sessionRef.current.token).catch(() => {});
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Initialize session
  useEffect(() => {
    initSession();
  }, []);

  const initSession = async () => {
    try {
      setState('loading');
      const newSession = await createMobileUploadSession(config.createSessionEndpoint);
      if (unmountedRef.current) return;

      setSession(newSession);
      // Use the WEB platform URL so the phone opens the mobile page on the web app
      const fullUrl = `${PLATFORM_URL}${newSession.mobileUploadPath}`;
      setQrUrl(fullUrl);
      setState('waiting');
      startPolling(newSession.token);
    } catch (err) {
      if (unmountedRef.current) return;
      setErrorMessage(getTranslation('upload.mobile.error'));
      setState('error');
    }
  };

  const startPolling = (token: string) => {
    pollingRef.current = setInterval(async () => {
      if (unmountedRef.current) {
        stopPolling();
        return;
      }

      try {
        const status = await getMobileUploadStatus(config.statusEndpoint, token);

        if (status.status === 'UPLOADED' && status.files.length > 0) {
          stopPolling();
          setState('received');

          // Download all uploaded files
          const downloadedFiles: File[] = [];
          for (const fileInfo of status.files) {
            try {
              const blob = await downloadMobileUploadedFile(
                config.downloadEndpoint,
                token,
                fileInfo.id
              );
              const file = new File(
                [blob],
                fileInfo.originalFilename || 'mobile-upload.jpg',
                { type: fileInfo.contentType || blob.type || 'image/jpeg' }
              );
              downloadedFiles.push(file);
            } catch {
              // Skip individual file download errors
            }
          }

          if (downloadedFiles.length > 0) {
            onFilesReceived(downloadedFiles);
          }

          // Auto-close after brief delay showing success
          setTimeout(() => {
            if (!unmountedRef.current) {
              onClose();
            }
          }, 1500);
        } else if (status.status === 'EXPIRED') {
          stopPolling();
          setState('expired');
        } else if (status.status === 'CANCELLED') {
          stopPolling();
          onClose();
        }
      } catch {
        // Network error during poll — keep polling unless unmounted
      }
    }, config.pollingIntervalMs);
  };

  const handleCancel = async () => {
    stopPolling();
    if (session) {
      cancelMobileUploadSession(config.cancelEndpoint, session.token).catch(() => {});
      setSession(null);
    }
    onClose();
  };

  return (
    <div className="mobile-upload-overlay" onClick={handleCancel}>
      <div className="mobile-upload-modal" onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <h3 className="mobile-upload-title">{getTranslation('upload.mobile.title')}</h3>

        {/* Content by state */}
        {state === 'loading' && (
          <div className="mobile-upload-loading">
            <div className="mobile-upload-spinner" />
          </div>
        )}

        {state === 'waiting' && (
          <>
            <p className="mobile-upload-description">
              {getTranslation('upload.mobile.description')}
            </p>
            <div className="mobile-upload-qr-container">
              <QRCodeSVG value={qrUrl} size={200} level="M" />
            </div>
            <p className="mobile-upload-expires">
              {getTranslation('upload.mobile.expires').replace(
                '{{minutes}}',
                String(config.expiresInMinutes)
              )}
            </p>
            <p className="mobile-upload-waiting">{getTranslation('upload.mobile.waiting')}</p>
          </>
        )}

        {state === 'received' && (
          <div className="mobile-upload-success">
            <span className="mobile-upload-success-icon">✓</span>
            <p>{getTranslation('upload.mobile.received')}</p>
          </div>
        )}

        {state === 'error' && (
          <div className="mobile-upload-error">
            <p>{errorMessage}</p>
          </div>
        )}

        {state === 'expired' && (
          <div className="mobile-upload-error">
            <p>{getTranslation('upload.mobile.expired')}</p>
          </div>
        )}

        {/* Cancel button */}
        {(state === 'waiting' || state === 'error' || state === 'expired') && (
          <button
            type="button"
            className="mobile-upload-cancel-btn"
            onClick={handleCancel}
          >
            {getTranslation('upload.mobile.cancel')}
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileUploadQrModal;
